import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { APIStatus } from "shared/ui/api-status/APIStatus"

import { Team } from "entities/teams/types/Team"

import { TeamListModel } from "./TeamList"
import { TeamListActionCell } from "./TeamListActionCell"
import { TeamListCell } from "./TeamListCell"
import { TeamListHeaderCell } from "./TeamListHeaderCell"
import { TeamListRoleCell } from "./TeamListRoleCell"
import { teamListTableFeatures } from "./TeamListTableFeatures"
import {
  TeamListViewContext,
  TeamListViewContextType
} from "./TeamListViewContext"
import "./styles/_team-list.scss"

export interface TeamListViewModel
  extends Pick<
    TeamListModel,
    | "teams"
    | "userID"
    | "handlers"
    | "getTeamHref"
    | "onTeamSelect"
    | "hasError"
  > {}

const columnHelper = createColumnHelper<typeof teamListTableFeatures, Team>()

// columnHelper.columns() keeps each column's own TValue instead of widening the
// array to a single ColumnDef type, which the per-cell CellContext props need.
const columns = columnHelper.columns([
  columnHelper.accessor("name", {
    cell: info => <TeamListCell info={info}>{info.getValue()}</TeamListCell>,
    header: () => <TeamListHeaderCell i18nKey="Team.Name" />
  }),
  columnHelper.accessor(row => row.memberships?.length, {
    id: "member-count",
    cell: info => <TeamListCell info={info}>{info.getValue()}</TeamListCell>,
    header: () => <TeamListHeaderCell i18nKey="Team.MemberCount" />
  }),
  columnHelper.accessor(row => row.memberships || [], {
    id: "current-user-role",
    cell: info => <TeamListRoleCell info={info} />,
    header: () => <TeamListHeaderCell i18nKey="Team.Role.Role" />
  }),
  columnHelper.accessor(row => row, {
    id: "team-actions",
    cell: info => <TeamListActionCell info={info} />,
    header: () => <TeamListHeaderCell i18nKey="Team.Actions.Actions" isHidden />
  })
])

export function TeamListView({
  teams,
  userID,
  handlers,
  hasError,
  getTeamHref,
  onTeamSelect
}: TeamListViewModel) {
  const { t } = useTranslation()
  const table = useTable({
    features: teamListTableFeatures,
    data: teams || [],
    columns
  })

  const viewContext = useMemo<TeamListViewContextType>(
    () => ({
      userID,
      getTeamHref,
      onTeamSelect,
      handlers
    }),
    [userID, handlers, getTeamHref, onTeamSelect]
  )

  if (hasError) {
    return (
      <APIStatus
        isUnavailable
        unavailableMessage={t("Team.ServiceUnavailable")}
      />
    )
  }

  if (!teams || teams.length === 0) {
    return <div className="team-list team-list--empty">{t("Team.Empty")}</div>
  }

  return (
    <TeamListViewContext.Provider value={viewContext}>
      <table className="team-list">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id} className="team-list__header-row">
              {headerGroup.headers.map(header => (
                <th key={header.id} className="team-list__header-cell">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className="team-list__row">
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="team-list__cell">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </TeamListViewContext.Provider>
  )
}
